'use client';

import { useInfiniteQuery } from '@tanstack/react-query';
import clsx from 'clsx';
import { FC, useCallback } from 'react';
import Button, {
  ButtonVariants,
} from '../../../../components/atoms/Button/Button';
import { BlogListingLink } from '../../../../components/organisms/BlogListingLink';
import useWindowSize from '../../../../hooks/useWindowSize';
import { showToast } from '../../../../lib/toast';
import { BlogPreview } from '../../../../types/blog';

interface AuthorArticlesAndBioProps {
  initialPosts: BlogPreview[];
  author: {
    id: number;
    bio?: string;
  };
  pageSize: number;
  hasInitialNextPage: boolean;
}

export const AuthorArticlesAndBioClient: FC<AuthorArticlesAndBioProps> = ({
  initialPosts,
  author: { bio, id: authorId },
  hasInitialNextPage,
  pageSize,
}) => {
  const { isMobile } = useWindowSize();

  const fetchMoreBlogPosts = useCallback(
    async ({ pageParam }: { pageParam: number }) => {
      const response = await fetch(
        `/api/articles-app?${new URLSearchParams({
          per_page: pageSize.toString(),
          authorId: authorId.toString(),
          page: pageParam.toString(),
          fields: ['id', 'title', 'excerpt', 'slug'].join(','),
        })}`
      );

      if (!response.ok) {
        showToast(
          'There was a problem fetching more articles. Please try again.',
          { type: 'error' }
        );
        throw new Error('Failed to fetch blog posts');
      }

      const {
        blogPosts,
        hasNextPage,
      }: { blogPosts: BlogPreview[]; hasNextPage: boolean } =
        await response.json();

      return {
        blogPosts,
        nextPage: hasNextPage ? pageParam + 1 : undefined,
      };
    },
    [authorId, pageSize]
  );

  const { data, fetchNextPage, hasNextPage, isFetchingNextPage, isFetched } =
    useInfiniteQuery({
      queryKey: ['relatedBlogPosts'],
      queryFn: fetchMoreBlogPosts,
      refetchOnWindowFocus: false,
      enabled: false,
      initialPageParam: 2,
      throwOnError: true,
      getNextPageParam: ({ nextPage }) => nextPage ?? undefined,
    });

  const showFetchMoreButton = isFetched ? hasNextPage : hasInitialNextPage;

  return (
    <div
      className={clsx(
        'flex flex-col gap-xl bg-neutral-100 md:flex-row-reverse md:px-[60px] md:py-[40px]',
        {
          'md:justify-between': !!bio,
          'md:justify-center': !bio,
        }
      )}
    >
      {bio && (
        <div className="flex flex-col gap-s rounded-small border-[1px] border-neutral-200 bg-white p-xl m:basis-1/3 md:self-start">
          <h3 className="text-h3SemiBold">About me</h3>
          <p className="text-body1Light">{bio}</p>
        </div>
      )}
      <div
        className={clsx('flex flex-col gap-xl px-l pb-[40px] md:px-[0px]', {
          'md:basis-2/3': !!bio,
        })}
      >
        <h3 className="text-h3SemiBold">Articles</h3>
        {initialPosts.map((post) => (
          <div className="flex w-full flex-row" key={post.id}>
            <BlogListingLink
              blog={post}
              layout={isMobile ? 'column' : 'row'}
              showExcerpt={!isMobile}
            />
          </div>
        ))}
        {data?.pages?.map((page) =>
          page.blogPosts.map((post) => (
            <div className="flex w-full flex-row" key={post.id}>
              <BlogListingLink
                blog={post}
                layout={isMobile ? 'column' : 'row'}
                showExcerpt={!isMobile}
              />
            </div>
          ))
        )}
        {showFetchMoreButton && (
          <div className="w-full self-center md:max-w-[262px]">
            <Button
              variant={ButtonVariants.Secondary}
              onClick={() => fetchNextPage()}
              disabled={isFetchingNextPage}
              loading={isFetchingNextPage}
              aria-label="Show more articles"
            >
              Show more
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};
